import { fetchBrokerData, fetchMetaApi } from '@/api/broker_metrics';
import type { BrokerAggregateStats, Metric } from '@/types/main';
import { computeDayWindowEnd, convertToMap, monthRange } from '@/utils/convert';
import {
    brokerDataBatchLoaded,
    brokerDataLoaded,
    brokerLoadingFinished,
    brokerLoadingStarted,
    brokerRemoved,
    brokersLoadingFinished,
    brokersLoadingStarted,
    brokersRemoved,
    metaLoaded,
    monthSelectionChanged,
    otherTabsInvalidated,
    tabLoadingFinished,
    tabLoadingStarted,
    type DateRange,
} from './brokerMetricsSlice';
import type { AppDispatch, RootState } from './store';

type BrokerFetchResult = { metricsMap: Record<string, Metric>; stats: BrokerAggregateStats };

const EMPTY_BROKER_STATS: BrokerAggregateStats = {
    median: null,
    percentile_90: null,
    percentile_99: null,
    percentile_10: null,
    standard_deviation: null,
};
const EMPTY_FETCH_RESULT: BrokerFetchResult = { metricsMap: {}, stats: EMPTY_BROKER_STATS };

// One or more brokers' chart buckets and server-aggregated stats for the
// same window, fetched together in a single call - a broker missing from
// the response (e.g. the whole request failed) falls back to an empty
// result rather than leaving its entry undefined. With no `dateRange` the
// window is the fixed today's-cutoff span; otherwise it's the caller's
// From/To dates, snapped to the day's edges.
async function fetchBrokersData(brokers: string[], endPoint: string, dateRange?: DateRange): Promise<Record<string, BrokerFetchResult>> {
    const endDate =
        dateRange && dateRange.end
            ? new Date(`${dateRange.end}T23:59:59.999Z`).toISOString()
            : computeDayWindowEnd().toISOString();
    const startDate = dateRange && dateRange.start ? new Date(`${dateRange.start}T00:00:00.000Z`).toISOString() : undefined;
    const response = await fetchBrokerData(brokers, endDate, endPoint, 'desc', startDate);
    return Object.fromEntries(
        brokers.map((broker) => {
            const entry = response[broker];
            return [broker, entry ? { metricsMap: convertToMap(entry.buckets, {}), stats: entry.stats } : EMPTY_FETCH_RESULT];
        })
    );
}

export function fetchMeta() {
    return async (dispatch: AppDispatch) => {
        const meta = await fetchMetaApi();
        if (meta) {
            dispatch(metaLoaded({ brokers: meta.brokers, endpoints: meta.end_points }));
        }
    };
}

export function fetchMissingBrokersForTab(endpoint: string) {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const state = getState().brokerMetrics;
        const tabCache = state.tabs[endpoint]?.metricsCache ?? {};
        const missing = state.comparedBrokers.filter((broker) => !tabCache[broker]);
        if (missing.length === 0) {
            return;
        }
        dispatch(tabLoadingStarted(endpoint));
        const results = await fetchBrokersData(missing, endpoint, state.dateRange);
        dispatch(brokerDataBatchLoaded({ endpoint, results }));
        dispatch(tabLoadingFinished(endpoint));
    };
}

// Applies a new month (and optional single day): a day narrows the window
// to that date, no day means the whole month. Drops every tab's cache,
// then refetches the compared brokers for the active tab so the chart
// updates immediately (other tabs refetch lazily when next viewed).
export function changeMonthSelection({ month, day }: { month: string; day: string }) {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const range: DateRange = day ? { start: day, end: day } : monthRange(month);
        dispatch(monthSelectionChanged({ month, day, range }));
        const { selectedEndpoint, comparedBrokers, dateRange } = getState().brokerMetrics;
        if (!selectedEndpoint || comparedBrokers.length === 0) {
            return;
        }
        dispatch(tabLoadingStarted(selectedEndpoint));
        const results = await fetchBrokersData(comparedBrokers, selectedEndpoint, dateRange);
        dispatch(brokerDataBatchLoaded({ endpoint: selectedEndpoint, results }));
        dispatch(tabLoadingFinished(selectedEndpoint));
    };
}

export function changeDateRangeSelection({ start, end }: DateRange) {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const month = start.slice(0, 7);
        const day = start === end ? start : '';
        dispatch(monthSelectionChanged({ month, day, range: { start, end } }));

        const { selectedEndpoint, comparedBrokers, dateRange } = getState().brokerMetrics;
        if (!selectedEndpoint || comparedBrokers.length === 0) {
            return;
        }

        dispatch(tabLoadingStarted(selectedEndpoint));
        const results = await fetchBrokersData(comparedBrokers, selectedEndpoint, dateRange);
        dispatch(brokerDataBatchLoaded({ endpoint: selectedEndpoint, results }));
        dispatch(tabLoadingFinished(selectedEndpoint));
    };
}

export function toggleBroker(broker: string) {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const state = getState().brokerMetrics;
        if (state.comparedBrokers.includes(broker)) {
            dispatch(brokerRemoved(broker));
            dispatch(otherTabsInvalidated(state.selectedEndpoint));
            return;
        }

        const endpoint = state.selectedEndpoint;
        const cachedMetrics = state.tabs[endpoint]?.metricsCache[broker];
        const cachedStats = state.tabs[endpoint]?.statsCache[broker];
        if (cachedMetrics && cachedStats) {
            dispatch(brokerDataLoaded({ endpoint, broker, metricsMap: cachedMetrics, stats: cachedStats }));
            dispatch(otherTabsInvalidated(endpoint));
            return;
        }

        dispatch(brokerLoadingStarted(broker));
        // Checking the box always selects the broker - even with no data
        // today it stays checked and shows up as a zero-data row, rather
        // than silently reverting to unchecked.
        const { metricsMap, stats } = (await fetchBrokersData([broker], endpoint, state.dateRange))[broker];
        dispatch(brokerDataLoaded({ endpoint, broker, metricsMap, stats }));
        dispatch(otherTabsInvalidated(endpoint));
        dispatch(brokerLoadingFinished(broker));
    };
}

export function toggleAllBrokers(brokerList: string[], shouldSelect: boolean) {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const state = getState().brokerMetrics;
        const endpoint = state.selectedEndpoint;

        if (!shouldSelect) {
            dispatch(brokersRemoved(brokerList));
            dispatch(otherTabsInvalidated(endpoint));
            return;
        }

        const missing = brokerList.filter((broker) => !state.comparedBrokers.includes(broker));
        if (missing.length === 0) {
            return;
        }
        dispatch(brokersLoadingStarted(missing));

        const tabCache = state.tabs[endpoint];
        const uncached = missing.filter((broker) => !(tabCache?.metricsCache[broker] && tabCache?.statsCache[broker]));
        const fetched = uncached.length > 0 ? await fetchBrokersData(uncached, endpoint, state.dateRange) : {};

        const results: Record<string, BrokerFetchResult> = {};
        missing.forEach((broker) => {
            const cachedMetrics = tabCache?.metricsCache[broker];
            const cachedStats = tabCache?.statsCache[broker];
            results[broker] = cachedMetrics && cachedStats ? { metricsMap: cachedMetrics, stats: cachedStats } : fetched[broker];
        });

        dispatch(brokerDataBatchLoaded({ endpoint, results }));
        dispatch(otherTabsInvalidated(endpoint));
        dispatch(brokersLoadingFinished(missing));
    };
}
